import HighchartsReact, {
  HighchartsReactProps,
  HighchartsReactRefObject,
} from "highcharts-react-official"
import Highcharts, { SeriesOptionsType } from "highcharts"
import { useEffect, useMemo, useRef } from "react"
import exportingModule from "highcharts/modules/exporting"

exportingModule(Highcharts)


const COLOR_PALETTE = [
  "#184389",          // DARK_BLUE
  "#4B99F7",          // SKY_BLUE
  "#73C9F9",          // LIGHT_CYAN
  "#235CE8",          // BLUE
  "#00A6FB",          // CYAN
  "#1C98B5",          // TEAL
]


export interface TmScoreGraphProps {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id?: string

  /**
   * List of requirements with their respective score
   */
  requirements: {
    name: string
    score: number
  }[]
  /**
   * List of vendors with their respective scores for each requirement
   */
  vendors: {
    name: string
    scores: number[]
  }[]
  /**
   * Whether to generate SVG or not
   */
  generate_svg?: boolean
  /**
   * SVG string
   */
  svg?: string
  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: (props: Partial<TmScoreGraphProps>) => void
}

const getOptions = (
  requirements: TmScoreGraphProps["requirements"],
  vendors: TmScoreGraphProps["vendors"]
): HighchartsReactProps["options"] => ({
  chart: {
    zooming: {
      type: "xy",
    },
    scrollablePlotArea: {
      minWidth: 35 * requirements.length,
      scrollPositionX: 1,
    },
  },
  credits: {
    enabled: false,
  },
  title: {
    text: "",
  },
  xAxis: [
    {
      categories: requirements.map((requirement) => requirement.name),
      labels: {
        style: {
          color: "#000000",
        },
      },
    },
  ],
  yAxis: [
    {
      title: {
        text: "values",
        style: {
          color: "#000000",
        },
      },
      labels: {
        format: "{value}",
        style: {
          color: "#000000",
        },
      },
      min: 0,
      max: 5,
      tickInterval: 1,
    },
  ],
  tooltip: {
    shared: true,
  },
  legend: {
    verticalAlign: "top",
    symbolRadius: 0,
  },
  plotOptions: {
    series: {
      marker: {
        enabled: true,
        symbol: "circle",
        radius: 3,
      },
      lineWidth: 1.5,
    },
    column: {
      borderColor: "#000000",
      color: "#00B4D8",
      opacity: 0.75,
      dataLabels: {
        enabled: true,
        inside: true,
        style: {
          color: "#000000",
          textOutline: "none",
          fontWeight: "bold"
        },
      }
    },
  },
  accessibility: {
    enabled: false,
  },

series: [
  {
    name: "Input Requirements",
    type: "column",
    yAxis: 0,
    tooltip: {},
    data: requirements.map((requirement) => requirement.score),
  },
  ...vendors.map(
    (vendor, index) =>
      ({
        name: vendor.name,
        type: "line",
        yAxis: 0,
        tooltip: {},
        data: vendor.scores,
        color: COLOR_PALETTE[index % COLOR_PALETTE.length],
        lineWidth: 2,
        marker: {
          enabled: true,
          symbol: "circle",
          radius: 5,
          lineColor: "#FFFFFF",
          lineWidth: 2,
        },
        shadow: {
          color: "#000000",
          width: 2,
          offsetX: 0,
          offsetY: 0,
          // opacity: 0.9,
        },
      } as SeriesOptionsType)
  ),
  ],
  exporting: {
    enabled: false,
  },
})

const TmScoreGraph = ({
  requirements,
  vendors,
  generate_svg = false,
  setProps,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ...props
}: TmScoreGraphProps) => {
  if (generate_svg == null) generate_svg = false

  const chartRef = useRef<HighchartsReactRefObject>(null)

  const options = useMemo(() => getOptions(requirements, vendors), [requirements, vendors])

  useEffect(() => {
    if (generate_svg) {
      const chart = chartRef.current?.chart
      if (chart != undefined) {
        setProps({ svg: chart.getSVG() })
      }
    }
  }, [generate_svg, options])

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
}

export default TmScoreGraph
